import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSessionStorage } from 'react-use';
import { titleCase } from 'title-case';
import { Host } from '../../../Host';
import { Icon } from '../../../concepts/resources/Icon';
import { Button, Colors, Toggle } from '../../../ui';
import { DebugInspect } from '../../../ui/DebugInspect';
import { FlexScroller, ScrollerInteral } from '../../../ui/Scrollers';
import { Copy, Flexer } from '../../dashboard/elements/LayoutElements';
import { AssistantContext } from './AssistantContext';
import { useAssitantModels, useExecuteAssitantToolCall } from './AssistantHooks';
import { AssistantMessage } from './AssistantMessage';
import { Chat } from './Chat';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatTypes';
import { ModelSelector } from './ModelSelector';
import { useChat } from './UseChat';
import { UserMessage } from './UserMessage';

const initialMessage: ChatMessage = {
    id: 'initial',
    role: 'assistant',
    content: [
        { type: 'text', text: 'Hey there, how can I help you today?' }
    ]
};

const getModeFromMessages = (messages: ChatMessage[]) => {
    const allPartsInReverse = messages.toReversed();
    for (const p of allPartsInReverse) {
        if (p.role === 'tool') {
            for (const c of p.content) {
                if (c.type === 'tool-result') {
                    if (c.toolName !== 'switchMode') continue;
                    const { response, mode } = c.result as any;
                    if (response === 'deny') continue;
                    return mode;
                }
            }
        }
    }
    return 'default';
};

const useAutoSetModeFromMessages = (messages: ChatMessage[], setMode: (mode: string) => void) => {
    const autoMode = useMemo(() => getModeFromMessages(messages), [messages]);
    useEffect(() => {
        setMode(autoMode);
    }, [autoMode]);
};

export const AssistantView = React.memo(() => {

    const [debug, setDebug] = useState(false);
    const { model, availableModels, switchModel } = useAssitantModels();
    const [mode, setMode] = useState('default');

    const [initialMessages, setInitialMessages] = useSessionStorage('temp/messages2', [] as ChatMessage[]);
    const [isComplete, setIsComplete] = useState(true);
    const [input, setInput] = useState('');

    const tryExecuteToolCall = useExecuteAssitantToolCall();

    const { messages, setMessages, addToolResult, append, submit } = useChat({
        api: `${Host.baseUrl}/api/v2/llm.chat`,
        initialMessages,
        onInitRequest({ url, data, ...rest }) {
            return {
                url: `${url}?model=${model?.id}&mode=${getModeFromMessages(data.messages)}`,
                data,
                ...rest,
            };
        },
        onFinish(msg) {
            console.log('onFinish', msg.id);

            if (msg.role === 'assistant') {
                for (const part of msg.content) {
                    if (part.type === 'tool-call') {
                        tryExecuteToolCall(part, result => {
                            // We need to do this a different way...
                            if (addToolResult(part.toolCallId, result)) {
                                submit();
                            }
                        });
                    }
                }
            }

            setIsComplete(true);
        },
        onError(error) {
            console.error(error);
            if (error.cause instanceof Error) {
                console.error(error.cause);
            }
        },
    });

    useEffect(() => {
        setInitialMessages(messages);
    }, [messages]);

    useAutoSetModeFromMessages(messages, setMode);

    const submitMessage = useCallback((text: string) => {
        append({
            role: 'user',
            content: [{
                type: 'text',
                text,
            }]
        });
        submit();
    }, [append, submit]);

    const submitToolResult = useCallback((toolCallId: string, result: any) => {
        if (addToolResult(toolCallId, result)) {
            submit();
        }
    }, [addToolResult, submit]);

    const onSubmitInput = useCallback(() => {
        append({
            role: 'user',
            content: [{
                type: 'text',
                text: input,
            }]
        });
        submit();
        setInput('');
        setIsComplete(false);

    }, [input, getSelection]);

    const onStop = () => {
        stop();
    };

    const onClear = () => {
        setInitialMessages([]);
        setMessages([]);
    };

    const scroller = useRef<ScrollerInteral>();
    useEffect(() => {
        const se = scroller.current?.getScrollElement();
        if (se) {
            se.scrollTo(0, se.scrollHeight);
        }
    }, [messages]);

    const contextValue = useMemo(() => ({
        messages,
        addToolResult,
        append,
        submit,
        submitMessage,
        submitToolResult,
    }), [messages, addToolResult, append, submit, submitMessage, submitToolResult]);

    return <Flexer flex="column" gap="1" width="44" height="48" padding="1">
        <Flexer flex="column" grow>
            <FlexScroller internalRef={scroller}>
                {debug && <>
                    <DebugInspect value={messages} />
                </>}
                {!debug && <>
                    <AssistantContext.Provider value={contextValue}>
                        <Chat>
                            <AssistantMessage message={initialMessage} />
                            {messages.map(x => (
                                <Flexer key={x.id} overflow="hidden">
                                    {x.role === 'user' && <UserMessage message={x} />}
                                    {x.role === 'assistant' && <AssistantMessage message={x} />}
                                </Flexer>
                            ))}
                        </Chat>
                    </AssistantContext.Provider>
                </>}
            </FlexScroller>
        </Flexer>
        <Flexer>
            <ChatInput value={input} onChange={setInput} onCommit={onSubmitInput}>
                <Flexer width="100%" flex="row" gap=".5" align="center" justify="space-between">
                    <Flexer flex="row" gap=".5" align="center">
                        <Flexer padding="0 .5">
                            <Copy flush size=".75" color={Colors.text} bold>
                                ({titleCase(mode)} Mode)
                            </Copy>
                        </Flexer>
                        <Flexer flex="row" gap=".5" align="center">
                            <Copy flush size=".75" color={Colors.text} bold>
                                Debug:
                            </Copy>
                            <Toggle size="sm" value={debug} onAction={setDebug} />
                        </Flexer>
                    </Flexer>
                    <Flexer grow flex="row" gap=".5" align="center" justify="flex-end">
                        <ModelSelector models={availableModels} selected={model} onChange={switchModel} />
                        <Button size="sm" onAction={onClear}>
                            <Icon size="1" src="deqo://icons/hero/trash" />
                        </Button>
                        {isComplete &&
                            <Button size="sm" variant="primary" onAction={onSubmitInput}>
                                <Icon size="1" src="deqo://icons/hero/arrow-up" />
                            </Button>
                        }
                        {!isComplete &&
                            <Button size="sm" variant="observe" onAction={onStop}>
                                <Icon size="1" src="deqo://icons/hero-mini/stop" />
                            </Button>
                        }
                    </Flexer>
                </Flexer>
            </ChatInput>
        </Flexer>
    </Flexer>;
});
